import { clamp } from '~/utils/math'

/**
 * Estimate a Mean Opinion Score (1.0–4.5) from network conditions using a
 * simplified ITU-T G.107 E-model. This is an approximation suitable for
 * relative monitoring/alerting, not a calibrated PESQ/POLQA score.
 *
 * Reference shape:
 *   effectiveLatency = oneWayDelay + 2 * jitter + 10  (ms)
 *   R drops with latency, then with packet loss (weighted ~2.5x per %)
 *   MOS = 1 + 0.035 R + R (R - 60)(100 - R) * 7e-6
 *
 * R starts at 93.2 (the ceiling for narrowband G.711-style codecs). The loss
 * weight (2.5/%) is G.711-oriented; WebRTC's Opus + PLC/FEC is more loss-
 * resilient, so this reads pessimistic under loss — fine for relative alerting.
 */
export const estimateMos = ({
  rttMs,
  jitterMs,
  lossPct,
}: {
  rttMs: number
  jitterMs: number
  lossPct: number
}): number => {
  const safeRtt = Number.isFinite(rttMs) && rttMs > 0 ? rttMs : 0
  const safeJitter = Number.isFinite(jitterMs) && jitterMs > 0 ? jitterMs : 0
  const safeLoss = Number.isFinite(lossPct) && lossPct > 0 ? lossPct : 0

  // One-way delay approximated as half the round-trip time.
  const effectiveLatency = safeRtt / 2 + 2 * safeJitter + 10

  // Latency penalty: gentle below 160ms, steeper beyond (classic E-model knee).
  let r = 93.2
  if (effectiveLatency < 160) {
    r -= effectiveLatency / 40
  } else {
    r -= (effectiveLatency - 120) / 10
  }

  // Packet loss is the dominant impairment for audio intelligibility.
  r -= 2.5 * safeLoss

  r = clamp(r, 0, 100)

  const mos = 1 + 0.035 * r + r * (r - 60) * (100 - r) * 7e-6

  // Clamp to the conventional MOS range.
  return clamp(mos, 1, 4.5)
}
